/**
 * FloatingChrome — the site's top shell (logo + language + menu trigger).
 *
 * It is placed at the very top of the document and scrolls away naturally
 * with the page: no fixed or sticky behaviour, no hide/reveal on scroll.
 * The mark and the controls sit directly in the composition: no plaques, no
 * boxes. On the dark photographic cover the original transparent logo is
 * reversed to silver-white with a CSS filter (the source PNG is a single
 * navy colour on full alpha, so the letterforms and wave are preserved
 * exactly). On light inner pages it renders in its original navy.
 */

import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { Menu as MenuIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MenuDialog from "@/components/nav/MenuDialog";
import { cn } from "@/utils/cn";

interface FloatingChromeProps {
  /** Visual treatment of the shell at the top of the page. */
  theme?: "dark" | "light";
}

const FloatingChrome = ({ theme = "dark" }: FloatingChromeProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const dark = theme === "dark" && !open;

  return (
    <>
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[60]">
        {/* Local scrim only — never a visible header strip */}
        <div
          aria-hidden="true"
          className={cn(
            "absolute inset-x-0 top-0 h-28 md:h-32",
            dark
              ? "bg-[linear-gradient(to_bottom,hsl(var(--ink-deep)/0.6),transparent)]"
              : "bg-[linear-gradient(to_bottom,hsl(var(--background)/0.85),transparent)]",
          )}
        />

        <div className="container relative mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between gap-3 py-4 md:py-6">

            <Link
              to="/"
              aria-label={t("nav.homeAria")}
              className={cn(
                "pointer-events-auto inline-flex items-center transition-opacity duration-200 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4",
                dark ? "focus-visible:outline-white" : "focus-visible:outline-cobalt",
              )}
            >
              <img
                src="/assets/oceannova-horizontal-logo.png"
                alt="OceanNova"
                width="1701"
                height="501"
                className={cn(
                  "h-auto w-[150px] transition-[filter,width] duration-500 sm:w-[176px] md:w-[210px] xl:w-[228px]",
                  dark
                    ? "[filter:brightness(0)_invert(1)_drop-shadow(0_1px_10px_hsl(var(--ink-deep)/0.55))]"
                    : "[filter:none]",
                )}
              />
            </Link>

            <div className="pointer-events-auto flex items-center gap-2 md:gap-3">
              <LanguageSwitcher variant={dark ? "light" : "dark"} />
              <span
                aria-hidden="true"
                className={cn(
                  "h-4 w-px transition-colors duration-500",
                  dark ? "bg-white/35" : "bg-foreground/25",
                )}
              />
              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={open}
                className={cn(
                  "inline-flex items-center gap-2 px-2 py-2 text-xs font-semibold uppercase tracking-[0.18em] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 md:text-sm",
                  dark
                    ? "text-white hover:text-cobalt-bright focus-visible:outline-white"
                    : "text-foreground hover:text-cobalt focus-visible:outline-cobalt",
                )}
              >
                {t("nav.menu")}
                <MenuIcon className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <MenuDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default memo(FloatingChrome);
